import type * as React from "react";
import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";

import { apiGet } from "@/lib/api";
import { BadgeToast } from "@/components/BadgeToast";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { HeaderProgressWidget } from "@/components/HeaderProgressWidget";
import { NotificationBell } from "@/components/NotificationBell";
import { AdminCapstonesPage } from "@/pages/AdminCapstonesPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AdminLearnerArchivePage } from "@/pages/AdminLearnerArchivePage";
import { AdminLearnerDetailPage } from "@/pages/AdminLearnerDetailPage";
import { AdminManualPage } from "@/pages/AdminManualPage";
import { CapstonePage } from "@/pages/CapstonePage";
import { CommunityCategoryPage } from "@/pages/CommunityCategoryPage";
import { CommunityConversationPage } from "@/pages/CommunityConversationPage";
import { CommunityDirectoryPage } from "@/pages/CommunityDirectoryPage";
import { CommunityHomePage } from "@/pages/CommunityHomePage";
import { CommunityMemberPage } from "@/pages/CommunityMemberPage";
import { CommunityMessagesPage } from "@/pages/CommunityMessagesPage";
import { CommunityMyProfilePage } from "@/pages/CommunityMyProfilePage";
import { CommunityNotificationsPage } from "@/pages/CommunityNotificationsPage";
import { CommunityThreadPage } from "@/pages/CommunityThreadPage";
import { AdminCommunityModerationPage } from "@/pages/AdminCommunityModerationPage";
import { CourseCompletePage } from "@/pages/CourseCompletePage";
import { CourseHome } from "@/pages/CourseHome";
import { ExamPage } from "@/pages/ExamPage";
import { Home } from "@/pages/Home";
import { LearnerHandbookPage } from "@/pages/LearnerHandbookPage";
import { LessonPlayer } from "@/pages/LessonPlayer";
import { ModuleQuizPage } from "@/pages/ModuleQuizPage";
import { MyJournalPage } from "@/pages/MyJournalPage";
import { PrivacyPolicy } from "@/pages/PrivacyPolicy";
import { ProgressPage } from "@/pages/ProgressPage";
import { TermsOfService } from "@/pages/TermsOfService";
import { VerifyPage } from "@/pages/VerifyPage";

export function App(): React.JSX.Element {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Brand gradient bar — the only place outside CTAs and milestone
       *   badges where the full indigo→purple→pink→orange spectrum
       *   shows. 4px tall, full width, sits above the sticky header so
       *   it scrolls away (the header stays). */}
      <div className="h-1 w-full bg-ca-gradient" aria-hidden="true" />

      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="flex items-center gap-3 text-foreground hover:opacity-80 transition-opacity"
            aria-label="Clinician Assist — CaiT Academy home"
          >
            <img
              src="/clinician-assist-logo.svg"
              alt=""
              aria-hidden="true"
              className="h-14 w-14 shrink-0 sm:h-16 sm:w-16"
            />
            <span className="flex flex-col leading-tight">
              <span className="font-display text-xl font-semibold tracking-tight sm:text-2xl">
                CaiT Academy
              </span>
              <span className="hidden text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground sm:inline">
                by Clinician Assist
              </span>
            </span>
          </Link>
          <nav className="flex items-center gap-5 text-sm font-medium text-muted-foreground">
            <Link to="/course" className="hover:text-foreground transition-colors">
              Course home
            </Link>
            <Link to="/journal" className="hover:text-foreground transition-colors">
              My Journal
            </Link>
            <Link to="/progress" className="hover:text-foreground transition-colors">
              My Progress
            </Link>
            <Link to="/community" className="hover:text-foreground transition-colors">
              Community
            </Link>
            <Link to="/handbook" className="hover:text-foreground transition-colors">
              Handbook
            </Link>
            <Link
              to="/privacy"
              className="hidden text-xs hover:text-foreground transition-colors xl:inline"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="hidden text-xs hover:text-foreground transition-colors xl:inline"
            >
              Terms
            </Link>
            <HeaderProgressWidget />
            <NotificationBell />
            <AdminNavLink />
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/course" element={<CourseHome />} />
          <Route
            path="/lesson/:moduleSlug/:lessonSlug"
            element={<LessonPlayer />}
          />
          <Route
            path="/module/:moduleSlug/quiz"
            element={<ModuleQuizPage />}
          />
          <Route path="/exam/:courseSlug" element={<ExamPage />} />
          {/*
            No-param /exam → redirect to the only Phase 0 course. Without
            this the bare /exam path falls through to the /:credentialId
            catch-all below and renders VerifyPage with "exam" treated as
            a cert ID — confusing dead-end. Same for /capstone.
          */}
          <Route
            path="/exam"
            element={<Navigate to="/exam/cait-foundations" replace />}
          />
          <Route
            path="/course/:courseSlug/complete"
            element={<CourseCompletePage />}
          />
          <Route path="/capstone/:courseSlug" element={<CapstonePage />} />
          <Route
            path="/capstone"
            element={<Navigate to="/capstone/cait-foundations" replace />}
          />
          <Route path="/journal" element={<MyJournalPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/community" element={<CommunityHomePage />} />
          <Route
            path="/community/c/:slug"
            element={<CommunityCategoryPage />}
          />
          <Route
            path="/community/t/:id"
            element={<CommunityThreadPage />}
          />
          <Route
            path="/community/messages"
            element={<CommunityMessagesPage />}
          />
          <Route
            path="/community/messages/:id"
            element={<CommunityConversationPage />}
          />
          <Route
            path="/community/directory"
            element={<CommunityDirectoryPage />}
          />
          <Route
            path="/community/me/profile"
            element={<CommunityMyProfilePage />}
          />
          <Route
            path="/community/members/:handleOrId"
            element={<CommunityMemberPage />}
          />
          <Route
            path="/community/notifications"
            element={<CommunityNotificationsPage />}
          />
          <Route
            path="/admin/community/moderation"
            element={<AdminCommunityModerationPage />}
          />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route
            path="/admin/learners/archived"
            element={<AdminLearnerArchivePage />}
          />
          <Route
            path="/admin/learners/:userId"
            element={<AdminLearnerDetailPage />}
          />
          <Route path="/admin/capstones" element={<AdminCapstonesPage />} />
          <Route path="/admin/handbook" element={<AdminManualPage />} />
          <Route path="/handbook" element={<LearnerHandbookPage />} />
          <Route path="/verify/:credentialId" element={<VerifyPage />} />
          {/*
            FR-H-2: cert QR codes encode the canonical verify URL
            ``verify.cait.academy/<credential_id>`` with no /verify/
            prefix. The Pages catch-all serves this SPA on that
            hostname, so we route bare-UUID paths to VerifyPage too.
            The UUID guard inside VerifyPage handles non-UUID
            fallthroughs (e.g. typo'd paths) cleanly as "not found".
            More-specific static routes above always win — react-router
            v6 ranks /privacy etc. above /:credentialId.
          */}
          <Route path="/:credentialId" element={<VerifyPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
        </Routes>
      </main>

      <footer className="border-t border-border bg-muted/40">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>
            © {new Date().getFullYear()}{" "}
            <a
              href="https://clinicianassist.ai"
              className="font-medium text-foreground hover:text-primary transition-colors"
            >
              Clinician Assist Inc.
            </a>{" "}
            — a Foundations-tier educational program. Not authorized for
            production clinical use.
          </p>
          <div className="flex gap-4">
            <Link to="/handbook" className="hover:text-foreground transition-colors">
              Handbook
            </Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>

      <BadgeToast />
      <CookieConsentBanner />
    </div>
  );
}

/**
 * Header link to the admin dashboard, rendered only for admins. Fetches
 * /api/auth/me once on mount; anonymous / non-admin (401/403/non-admin
 * role) just renders nothing.
 */
function AdminNavLink(): React.JSX.Element | null {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void apiGet<{ role: string }>("/api/auth/me")
      .then((me) => {
        if (!cancelled && me.role === "admin") setIsAdmin(true);
      })
      .catch(() => {
        // anon / non-admin → no link
      });
    return () => {
      cancelled = true;
    };
  }, []);
  if (!isAdmin) return null;
  return (
    <Link to="/admin" className="hover:text-foreground transition-colors">
      Admin
    </Link>
  );
}
